<template>
    <div>
        <div class="mb-3">
            <b-button
                variant="outline-primary"
                size="sm"
                @click="addParticipant"
            >
                <i class="fas fa-plus"></i> Добавить {{ participantLabel }}
            </b-button>
            <small v-if="hintText" class="text-muted d-block mt-1">
                {{ hintText }}
            </small>
            <small v-else class="text-muted d-block mt-1">
                Порядок определяет приоритет: первый в списке - основной, остальные - запасные (на случай отсутствия/болезни)
            </small>
        </div>

        <div v-if="localParticipants.length">
            <div
                v-for="(participant, index) in localParticipants"
                :key="participant._uid || index"
                class="participant-config-card position-relative"
                :class="{ 'participant-config-card--manager': showManagerPermissions }"
            >
                <div v-if="showManagerPermissions" class="participant-config-card__header">
                    <div class="participant-config-card__title">
                        <span class="participant-config-card__number">{{ index + 1 }}</span>
                        <span>Управляющий {{ index + 1 }}</span>
                    </div>
                    <small class="participant-config-card__summary">
                        Ролей выбрано: {{ selectedManagedRoleOptions(index).length }}
                    </small>
                </div>

                <!-- Индикатор приоритета -->
                <div v-if="showPriority" class="participant-config-card__priority">
                    <b-badge :variant="index === 0 ? 'primary' : 'secondary'">
                        {{ index === 0 ? 'Основной' : `Запасной ${index}` }}
                    </b-badge>
                </div>

                <!-- Кнопки управления порядком и удаления -->
                <div class="participant-config-card__actions">
                    <b-button-group size="sm">
                        <b-button
                            v-if="showPriority"
                            variant="outline-secondary"
                            @click="moveUp(index)"
                            :disabled="index === 0"
                            title="Повысить приоритет"
                        >
                            <i class="fas fa-arrow-up"></i>
                        </b-button>
                        <b-button
                            v-if="showPriority"
                            variant="outline-secondary"
                            @click="moveDown(index)"
                            :disabled="index === localParticipants.length - 1"
                            title="Понизить приоритет"
                        >
                            <i class="fas fa-arrow-down"></i>
                        </b-button>
                        <b-button
                            variant="outline-danger"
                            @click="removeParticipant(index)"
                            title="Удалить"
                        >
                            <i class="fas fa-times"></i>
                        </b-button>
                    </b-button-group>
                </div>

                <!-- Конфигурация участника -->
                <div
                    class="participant-config-card__body"
                    :class="{ 'participant-config-card__body--priority': showPriority && !showManagerPermissions }"
                >
                    <ParticipantConfig
                        :value="localParticipants[index]"
                        :users="users"
                        :departments="departments"
                        :participant-label="participantLabel"
                        :show-object-roles="showObjectRoles"
                        :show-request-roles="showRequestRoles"
                        :show-department-head="showDepartmentHead"
                        :type-label="typeLabel"
                        :all-text="allText"
                        :show-notification-mode="showNotificationMode"
                        :allowed-types="allowedTypes"
                        :object-role-options="objectRoleOptions"
                        :object-role-hint-text="objectRoleHintText"
                        @input="updateParticipantConfig(index, $event)"
                    />

                    <div v-if="showManagerPermissions" class="manager-permissions mt-3">
                        <div class="manager-permissions__head">
                            <div>
                                <strong>Права этого управляющего</strong>
                                <small class="text-muted d-block">
                                    Настройте, какие роли он может менять и какие операции ему доступны.
                                </small>
                            </div>
                            <b-button-group size="sm">
                                <b-button variant="outline-secondary" @click="selectAllManagedRoles(index)">
                                    Все роли
                                </b-button>
                                <b-button variant="outline-secondary" @click="clearManagedRoles(index)">
                                    Очистить
                                </b-button>
                            </b-button-group>
                        </div>

                        <b-form-group label="Какие роли может менять">
                            <b-form-checkbox-group
                                v-model="localParticipants[index].managed_roles"
                                class="manager-permissions__checks"
                                @change="updateManagedRoles(index, $event)"
                            >
                                <b-form-checkbox
                                    v-for="role in resolvedManagedRoleOptions"
                                    :key="role.value"
                                    :value="role.value"
                                >
                                    {{ role.text }}
                                </b-form-checkbox>
                            </b-form-checkbox-group>
                        </b-form-group>

                        <div class="manager-role-permissions">
                            <div class="manager-role-permissions__title">
                                Что может делать по каждой выбранной роли
                            </div>
                            <div
                                v-if="selectedManagedRoleOptions(index).length"
                                class="manager-role-permissions__grid"
                            >
                                <div class="manager-role-permissions__header">Роль</div>
                                <div
                                    v-for="operation in managerPermissionOptions"
                                    :key="'head-' + operation.key"
                                    class="manager-role-permissions__header manager-role-permissions__header--operation"
                                >
                                    {{ operation.shortText }}
                                </div>

                                <template v-for="role in selectedManagedRoleOptions(index)">
                                    <div
                                        :key="'role-' + role.value"
                                        class="manager-role-permissions__role"
                                    >
                                        {{ role.text }}
                                    </div>
                                    <div
                                        v-for="operation in managerPermissionOptions"
                                        :key="role.value + '-' + operation.key"
                                        class="manager-role-permissions__operation"
                                    >
                                        <b-form-checkbox
                                            v-model="localParticipants[index].role_operations[role.value][operation.key]"
                                            :aria-label="role.text + ': ' + operation.text"
                                            @change="emitUpdate"
                                        >
                                            <span class="manager-role-permissions__mobile-label">
                                                {{ operation.text }}
                                            </span>
                                        </b-form-checkbox>
                                    </div>
                                </template>
                            </div>
                            <div v-else class="manager-role-permissions__empty">
                                Выберите хотя бы одну роль, чтобы настроить действия.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-muted text-center py-3 border rounded">
            <i class="fas fa-info-circle"></i>
            {{ resolvedEmptyText }}
        </div>

        <!-- Опция назначения любому сотруднику отдела -->
        <div v-if="showFallbackOption && localParticipants.length > 0" class="mt-3">
            <b-form-checkbox v-model="allowAnyDepartmentEmployee" @change="emitUpdate" switch>
                Можно назначить любому сотруднику отдела, если список закончился
            </b-form-checkbox>
            <small class="text-muted d-block mt-1">
                Если все указанные участники недоступны (болезнь, отпуск), система сможет автоматически 
                назначить заявку на любого доступного сотрудника соответствующего отдела
            </small>
        </div>
    </div>
</template>

<script>
import ParticipantConfig from '../ParticipantConfig/ParticipantConfig.vue';

function cloneParticipants(participants) {
    return JSON.parse(JSON.stringify(participants || []));
}

const DEFAULT_MANAGER_ROLE_OPTIONS = [
    { value: 'initiator', text: 'Инициатор' },
    { value: 'approver', text: 'Согласующий' },
    { value: 'assigner', text: 'Назначающий' },
    { value: 'executor', text: 'Исполнитель' },
    { value: 'observer', text: 'Наблюдатель' }
];

const MANAGER_PERMISSION_KEYS = [
    'canReplaceSameDepartment',
    'canReplaceAnyEmployee',
    'canAddAnyEmployee',
    'canRemove'
];

const MANAGER_PERMISSION_OPTIONS = [
    {
        key: 'canReplaceSameDepartment',
        text: 'Замена в том же отделе',
        shortText: 'Замена в отделе'
    },
    {
        key: 'canReplaceAnyEmployee',
        text: 'Замена на любого сотрудника',
        shortText: 'Замена на любого'
    },
    {
        key: 'canAddAnyEmployee',
        text: 'Добавление участника',
        shortText: 'Добавление'
    },
    {
        key: 'canRemove',
        text: 'Удаление участника',
        shortText: 'Удаление'
    }
];

const MANAGER_PERMISSION_SNAKE_KEYS = {
    canReplaceSameDepartment: 'can_replace_same_department',
    canReplaceAnyEmployee: 'can_replace_any_employee',
    canAddAnyEmployee: 'can_add_any_employee',
    canRemove: 'can_remove'
};

export default {
    name: "MultipleParticipantsConfig",
    components: {
        ParticipantConfig
    },
    props: {
        value: {
            type: Object,
            default: () => ({ participants: [], allow_any_department_employee: false })
        },
        users: {
            type: Array,
            default: () => []
        },
        departments: {
            type: Array,
            default: () => []
        },
        participantLabel: {
            type: String,
            default: 'участника'
        },
        showObjectRoles: {
            type: Boolean,
            default: false
        },
        showRequestRoles: {
            type: Boolean,
            default: false
        },
        showDepartmentHead: {
            type: Boolean,
            default: false
        },
        showFallbackOption: {
            type: Boolean,
            default: true
        },
        showPriority: {
            type: Boolean,
            default: true
        },
        hintText: {
            type: String,
            default: null
        },
        emptyText: {
            type: String,
            default: null
        },
        typeLabel: {
            type: String,
            default: null
        },
        allText: {
            type: String,
            default: null
        },
        showNotificationMode: {
            type: Boolean,
            default: false
        },
        allowedTypes: {
            type: Array,
            default: () => null
        },
        objectRoleOptions: {
            type: Array,
            default: () => null
        },
        objectRoleHintText: {
            type: String,
            default: null
        },
        showManagerPermissions: {
            type: Boolean,
            default: false
        },
        managedRoleOptions: {
            type: Array,
            default: () => DEFAULT_MANAGER_ROLE_OPTIONS.map(option => ({ ...option }))
        },
        defaultManagedRoles: {
            type: Array,
            default: () => DEFAULT_MANAGER_ROLE_OPTIONS.map(option => option.value)
        },
        defaultManagerPermissions: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            localParticipants: this.value && this.value.participants && this.value.participants.length > 0 
                ? this.normalizeParticipants(this.value.participants)
                : [],
            allowAnyDepartmentEmployee: this.value && this.value.allow_any_department_employee !== undefined
                ? this.value.allow_any_department_employee
                : false
        }
    },
    computed: {
        resolvedEmptyText() {
            return this.emptyText || `Участники не добавлены. Нажмите "Добавить ${this.participantLabel}" для настройки.`;
        },
        resolvedManagedRoleOptions() {
            return Array.isArray(this.managedRoleOptions) && this.managedRoleOptions.length
                ? this.managedRoleOptions
                : DEFAULT_MANAGER_ROLE_OPTIONS.map(option => ({ ...option }));
        },
        managerPermissionOptions() {
            return MANAGER_PERMISSION_OPTIONS;
        }
    },
    watch: {
        value: {
            handler(newVal) {
                if (newVal && newVal.participants && Array.isArray(newVal.participants)) {
                    this.localParticipants = this.normalizeParticipants(newVal.participants);
                }
                if (newVal && newVal.allow_any_department_employee !== undefined) {
                    this.allowAnyDepartmentEmployee = newVal.allow_any_department_employee;
                }
            },
            deep: true
        }
    },
    methods: {
        getDefaultParticipantType() {
            return Array.isArray(this.allowedTypes) && this.allowedTypes.length
                ? this.allowedTypes[0]
                : 'all';
        },
        normalizeParticipants(participants) {
            return cloneParticipants(participants).map(participant => this.normalizeManagerParticipant(participant));
        },
        normalizeManagedRoles(roles) {
            const roleOptions = Array.isArray(this.managedRoleOptions) && this.managedRoleOptions.length
                ? this.managedRoleOptions
                : DEFAULT_MANAGER_ROLE_OPTIONS;
            const allowedRoles = roleOptions.map(option => option.value);
            const seen = {};
            return (Array.isArray(roles) ? roles : [])
                .filter(role => allowedRoles.includes(role))
                .filter(role => {
                    if (seen[role]) {
                        return false;
                    }
                    seen[role] = true;
                    return true;
                });
        },
        getDefaultManagerPermission(key) {
            const snakeKey = MANAGER_PERMISSION_SNAKE_KEYS[key];
            return this.defaultManagerPermissions &&
                (this.defaultManagerPermissions[key] === true || this.defaultManagerPermissions[snakeKey] === true);
        },
        hasExplicitManagerOperations(source) {
            const config = source || {};
            return MANAGER_PERMISSION_KEYS.some(key => {
                const snakeKey = MANAGER_PERMISSION_SNAKE_KEYS[key];
                return Object.prototype.hasOwnProperty.call(config, key) ||
                    Object.prototype.hasOwnProperty.call(config, snakeKey);
            });
        },
        getRoleOperationsSource(participant) {
            const config = participant || {};
            const source = config.role_operations ||
                config.roleOperations ||
                config.managed_role_operations ||
                config.managedRoleOperations ||
                {};
            return source && typeof source === 'object' && !Array.isArray(source)
                ? source
                : {};
        },
        hasExplicitRoleOperations(participant) {
            return Object.keys(this.getRoleOperationsSource(participant)).length > 0;
        },
        getOperationValue(source, key) {
            const config = source || {};
            const snakeKey = MANAGER_PERMISSION_SNAKE_KEYS[key];
            return config[key] === true || config[snakeKey] === true;
        },
        buildEmptyManagerOperations() {
            return MANAGER_PERMISSION_KEYS.reduce((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
        },
        buildLegacyManagerOperations(participant) {
            const source = this.hasExplicitManagerOperations(participant)
                ? participant
                : this.defaultManagerPermissions;
            return MANAGER_PERMISSION_KEYS.reduce((acc, key) => {
                acc[key] = this.getOperationValue(source, key);
                return acc;
            }, {});
        },
        normalizeRoleOperations(participant, roles) {
            const roleOperationsSource = this.getRoleOperationsSource(participant);
            const hasRoleOperations = Object.keys(roleOperationsSource).length > 0;
            const legacyOperations = hasRoleOperations
                ? this.buildEmptyManagerOperations()
                : this.buildLegacyManagerOperations(participant);

            return this.normalizeManagedRoles(roles).reduce((acc, role) => {
                const roleSource = roleOperationsSource[role] || {};
                acc[role] = MANAGER_PERMISSION_KEYS.reduce((operations, key) => {
                    operations[key] = hasRoleOperations
                        ? this.getOperationValue(roleSource, key)
                        : legacyOperations[key] === true;
                    return operations;
                }, {});
                return acc;
            }, {});
        },
        normalizeSelectedRoleOperations(index) {
            const participant = this.localParticipants[index];
            if (!participant) return;

            const roles = this.normalizeManagedRoles(participant.managed_roles);
            const currentRoleOperations = participant.role_operations || {};
            const nextRoleOperations = roles.reduce((acc, role) => {
                const existing = currentRoleOperations[role] || {};
                acc[role] = MANAGER_PERMISSION_KEYS.reduce((operations, key) => {
                    operations[key] = existing[key] === true;
                    return operations;
                }, {});
                return acc;
            }, {});

            this.$set(participant, 'managed_roles', roles);
            this.$set(participant, 'role_operations', nextRoleOperations);
        },
        normalizeManagerParticipant(participant) {
            if (!this.showManagerPermissions) {
                return participant;
            }

            const normalized = { ...participant };
            const rawRoles = Array.isArray(normalized.managed_roles)
                ? normalized.managed_roles
                : (Array.isArray(normalized.managedRoles) ? normalized.managedRoles : this.defaultManagedRoles);
            normalized.managed_roles = this.normalizeManagedRoles(rawRoles);
            delete normalized.managedRoles;
            normalized.role_operations = this.normalizeRoleOperations(normalized, normalized.managed_roles);
            delete normalized.roleOperations;
            delete normalized.managed_role_operations;
            delete normalized.managedRoleOperations;

            MANAGER_PERMISSION_KEYS.forEach(key => {
                const snakeKey = MANAGER_PERMISSION_SNAKE_KEYS[key];
                delete normalized[key];
                delete normalized[snakeKey];
            });

            return normalized;
        },
        addParticipant() {
            const participant = this.normalizeManagerParticipant({
                _uid: Date.now() + Math.random(),
                type: this.getDefaultParticipantType(),
                priority: this.localParticipants.length,
                ...(this.showManagerPermissions
                    ? {
                        managed_roles: [],
                        role_operations: {}
                    }
                    : {})
            });
            this.localParticipants.push(participant);
            this.emitUpdate();
        },
        selectAllManagedRoles(index) {
            if (!this.localParticipants[index]) return;
            this.$set(
                this.localParticipants[index],
                'managed_roles',
                this.resolvedManagedRoleOptions.map(option => option.value)
            );
            this.normalizeSelectedRoleOperations(index);
            this.emitUpdate();
        },
        clearManagedRoles(index) {
            if (!this.localParticipants[index]) return;
            this.$set(this.localParticipants[index], 'managed_roles', []);
            this.$set(this.localParticipants[index], 'role_operations', {});
            this.emitUpdate();
        },
        updateManagedRoles(index, roles) {
            if (this.localParticipants[index] && Array.isArray(roles)) {
                this.$set(this.localParticipants[index], 'managed_roles', roles);
            }
            this.normalizeSelectedRoleOperations(index);
            this.emitUpdate();
        },
        selectedManagedRoleOptions(index) {
            const participant = this.localParticipants[index] || {};
            const roles = this.normalizeManagedRoles(participant.managed_roles);
            return this.resolvedManagedRoleOptions.filter(option => roles.includes(option.value));
        },
        updateParticipantConfig(index, value) {
            const current = this.localParticipants[index] || {};
            const next = { ...(value || {}) };

            if (this.showManagerPermissions) {
                if (!Object.prototype.hasOwnProperty.call(next, 'managed_roles')) {
                    next.managed_roles = current.managed_roles || [];
                }
                next.role_operations = current.role_operations || {};
            }

            this.$set(this.localParticipants, index, this.normalizeManagerParticipant(next));
            this.emitUpdate();
        },
        removeParticipant(index) {
            this.localParticipants.splice(index, 1);
            // Обновляем приоритеты
            this.localParticipants.forEach((p, i) => {
                p.priority = i;
            });
            this.emitUpdate();
        },
        moveUp(index) {
            if (index > 0) {
                const temp = this.localParticipants[index];
                this.localParticipants.splice(index, 1);
                this.localParticipants.splice(index - 1, 0, temp);
                // Обновляем приоритеты
                this.localParticipants.forEach((p, i) => {
                    p.priority = i;
                });
                this.emitUpdate();
            }
        },
        moveDown(index) {
            if (index < this.localParticipants.length - 1) {
                const temp = this.localParticipants[index];
                this.localParticipants.splice(index, 1);
                this.localParticipants.splice(index + 1, 0, temp);
                // Обновляем приоритеты
                this.localParticipants.forEach((p, i) => {
                    p.priority = i;
                });
                this.emitUpdate();
            }
        },
        emitUpdate() {
            this.$emit('input', {
                participants: cloneParticipants(this.localParticipants),
                allow_any_department_employee: this.allowAnyDepartmentEmployee
            });
        }
    }
}
</script>

<style scoped>
.participant-config-card {
    position: relative;
    padding: 1rem;
    margin-bottom: 0.75rem;
    border: 1px solid #dfe5ec;
    border-radius: 6px;
    background: #ffffff;
}

.participant-config-card + .participant-config-card {
    margin-top: 1rem;
}

.participant-config-card--manager {
    padding: 0;
    overflow: hidden;
    border-color: #c9d9ee;
    border-left: 4px solid #2f80ed;
    background: #fbfdff;
    box-shadow: 0 2px 8px rgba(42, 66, 93, 0.08);
}

.participant-config-card--manager:nth-child(even) {
    border-color: #c9e3d9;
    border-left-color: #23966f;
    background: #fbfffd;
}

.participant-config-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 48px;
    padding: 10px 56px 10px 14px;
    border-bottom: 1px solid #e4ebf3;
    background: #f1f6fc;
}

.participant-config-card--manager:nth-child(even) .participant-config-card__header {
    border-bottom-color: #e0efe9;
    background: #f0faf5;
}

.participant-config-card__title {
    display: flex;
    align-items: center;
    min-width: 0;
    color: #24364b;
    font-weight: 700;
}

.participant-config-card__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    margin-right: 8px;
    border-radius: 50%;
    background: #2f80ed;
    color: #ffffff;
    font-size: 0.82rem;
    line-height: 1;
}

.participant-config-card--manager:nth-child(even) .participant-config-card__number {
    background: #23966f;
}

.participant-config-card__summary {
    flex: 0 0 auto;
    color: #5e6f83;
    font-weight: 600;
}

.participant-config-card__priority {
    position: absolute;
    top: 10px;
    left: 10px;
}

.participant-config-card__actions {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
}

.participant-config-card__body {
    padding: 0;
}

.participant-config-card__body--priority {
    margin-top: 1.5rem;
}

.participant-config-card--manager .participant-config-card__body {
    padding: 12px 14px 14px;
}

.manager-permissions {
    padding: 12px;
    border: 1px solid #d6e2f0;
    border-radius: 4px;
    background: #ffffff;
}

.manager-permissions__head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
}

.manager-permissions__checks {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 6px 12px;
}

.manager-role-permissions {
    margin-top: 10px;
}

.manager-role-permissions__title {
    margin-bottom: 6px;
    color: #4b5d73;
    font-size: 0.85rem;
    font-weight: 700;
}

.manager-role-permissions__grid {
    display: grid;
    grid-template-columns: minmax(150px, 1fr) repeat(4, minmax(110px, 0.8fr));
    border: 1px solid #dfe5ec;
    border-radius: 4px;
    overflow: hidden;
    background: #ffffff;
}

.manager-role-permissions__header,
.manager-role-permissions__role,
.manager-role-permissions__operation {
    min-width: 0;
    padding: 8px 10px;
    border-right: 1px solid #edf1f5;
    border-bottom: 1px solid #edf1f5;
}

.manager-role-permissions__header {
    background: #eef3f8;
    color: #4b5d73;
    font-size: 0.78rem;
    font-weight: 700;
}

.manager-role-permissions__header--operation {
    text-align: center;
}

.manager-role-permissions__role {
    font-weight: 700;
}

.manager-role-permissions__operation {
    display: flex;
    justify-content: center;
}

.manager-role-permissions__grid > :nth-last-child(-n + 5) {
    border-bottom: 0;
}

.manager-role-permissions__grid > :nth-child(5n) {
    border-right: 0;
}

.manager-role-permissions__mobile-label {
    display: none;
}

.manager-role-permissions__empty {
    padding: 10px 12px;
    border: 1px dashed #c7d0da;
    border-radius: 4px;
    background: #ffffff;
    color: #6c757d;
    font-size: 0.86rem;
}

@media (max-width: 575.98px) {
    .participant-config-card {
        padding: 0.8rem;
    }

    .participant-config-card--manager {
        padding: 0;
    }

    .participant-config-card__header {
        display: block;
        min-height: 0;
        padding: 10px 48px 10px 12px;
    }

    .participant-config-card__summary {
        display: block;
        margin-top: 4px;
        padding-left: 34px;
    }

    .participant-config-card--manager .participant-config-card__body {
        padding: 10px 12px 12px;
    }

    .manager-permissions__head {
        flex-direction: column;
    }

    .manager-role-permissions__grid {
        display: block;
        border: 0;
        background: transparent;
    }

    .manager-role-permissions__header {
        display: none;
    }

    .manager-role-permissions__role {
        margin-top: 8px;
        padding: 8px 10px;
        border: 1px solid #dfe5ec;
        border-bottom: 0;
        border-radius: 4px 4px 0 0;
        background: #eef3f8;
    }

    .manager-role-permissions__operation {
        display: block;
        padding: 6px 10px;
        border: 1px solid #dfe5ec;
        border-top: 0;
        background: #ffffff;
    }

    .manager-role-permissions__mobile-label {
        display: inline;
    }
}
</style>
